import React, { useState, useEffect, useCallback } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Chip,
  Button,
  Divider,
  Paper,
  Stack,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SyncIcon from "@mui/icons-material/Sync";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DescriptionIcon from "@mui/icons-material/Description";
import VerifiedIcon from "@mui/icons-material/Verified";
import PersonIcon from "@mui/icons-material/Person";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import {
  syncCandidateReplies,
  uploadManualDocument,
  batchManualVerify,
  getPreOnboardings,
} from "../../services/preonboardingService";
import useAuth from "../../hooks/useAuth";

const statusColor = (status) => {
  switch (status) {
    case "DOCUMENT_VERIFICATION_COMPLETED":
    case "VERIFIED":
      return { bgcolor: "#DEF7EC", color: "#03543F", label: "Verified" };
    case "AI_FAILED":
    case "NOT_VERIFIED":
    case "FAILED":
      return { bgcolor: "#FEE2E2", color: "#991B1B", label: "AI Verification Failed" };
    case "DOCUMENT_COLLECTION_PENDING":
    case "NOT_SUBMITTED":
      return { bgcolor: "#FEF08A", color: "#854D0E", label: "Pending Submission" };
    case "RECEIVED":
    case "SUBMITTED":
    case "DOCUMENT_VERIFICATION_PENDING":
      return { bgcolor: "#E1EFFE", color: "#1E429F", label: "Under Review" };
    case "REJECTED":
      return { bgcolor: "#FDE8E8", color: "#9B1C1C", label: "Rejected" };
    default:
      return { bgcolor: "#F3F4F6", color: "#374151", label: status ? status.replace(/_/g, " ") : "Pending" };
  }
};

const isImageDoc = (url) => {
  if (!url) return false;
  const cleanUrl = url.split("?")[0].toLowerCase();
  return (
    cleanUrl.endsWith(".png") ||
    cleanUrl.endsWith(".jpg") ||
    cleanUrl.endsWith(".jpeg") ||
    cleanUrl.endsWith(".webp") ||
    cleanUrl.endsWith(".svg")
  );
};

const CandidateDocumentDrawer = ({ open, onClose, candidate, onUpdated }) => {
  const { user, organisationId, customerId } = useAuth();
  const currentUserId = parseInt(user?.id || user?.user_id || user?.userId || 1, 10);
  const orgId = organisationId || user?.org_id || user?.organisation_id || 307;
  const custId = customerId || user?.customer_id || user?.cust_id || 394;

  const [localCandidate, setLocalCandidate] = useState(candidate);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploadingDocType, setUploadingDocType] = useState(null);
  const [feedback, setFeedback] = useState({ open: false, message: "", severity: "success" });

  // Manual Review Dialog
  const [reviewDialog, setReviewDialog] = useState({
    open: false,
    doc: null,
    status: "VERIFIED",
    reason: "",
    submitting: false,
  });

  // In-Screen Document Preview Modal
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewZoom, setPreviewZoom] = useState(1);

  const handleDownloadDoc = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || "document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, "_blank");
    }
  };

  const reloadCandidateData = useCallback(async () => {
    if (!candidate) return;
    const targetCandidateId = candidate.candidate_id || candidate.profile_id;
    const targetPreOnboardingId = candidate.pre_onboarding_id || candidate.id;

    try {
      setLoadingDetails(true);
      const res = await getPreOnboardings({
        orgId: candidate.org_id || orgId,
        customerId: candidate.customer_id || custId,
        page: 1,
        maxRows: 100,
      });

      const list = res?.data || [];
      const found = list.find(
        (c) =>
          (targetPreOnboardingId && (c.pre_onboarding_id === targetPreOnboardingId || c.id === targetPreOnboardingId)) ||
          (targetCandidateId && (c.candidate_id === targetCandidateId || String(c.candidate_id) === String(targetCandidateId)))
      );

      if (found) {
        setLocalCandidate(found);
      }
    } catch (err) {
      console.warn("Could not reload candidate details from pre-onboardings:", err);
    } finally {
      setLoadingDetails(false);
    }
  }, [candidate, orgId, custId]);

  useEffect(() => {
    setLocalCandidate(candidate);
    if (open && candidate) {
      const hasDocs = Array.isArray(candidate.required_documents) && candidate.required_documents.length > 0;
      if (!hasDocs) {
        reloadCandidateData();
      }
    }
  }, [candidate, open, reloadCandidateData]);

  if (!candidate) return null;

  const activeCandidate = localCandidate || candidate;
  const preOnboardingId = activeCandidate.pre_onboarding_id || activeCandidate.id || activeCandidate.candidate_id;
  const reqDocs = activeCandidate.required_documents || [];
  const overallStatus = activeCandidate.status || activeCandidate.pre_onboarding_status;
  const statusInfo = statusColor(overallStatus);

  const handleSyncReplies = async () => {
    if (!preOnboardingId) return;
    setSyncing(true);
    setFeedback({ open: false, message: "", severity: "success" });
    try {
      const res = await syncCandidateReplies(preOnboardingId);
      setFeedback({
        open: true,
        message: res?.message || "Sync completed! Checked for new email attachments.",
        severity: "success",
      });
      await reloadCandidateData();
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error("Error syncing replies:", err);
      setFeedback({
        open: true,
        message: err.response?.data?.detail || err.message || "Failed to sync candidate replies.",
        severity: "error",
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleFileUpload = async (event, docType) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingDocType(docType);
    setFeedback({ open: false, message: "", severity: "success" });
    try {
      const uploadRes = await uploadManualDocument(preOnboardingId, docType, file);
      setFeedback({
        open: true,
        message: `Successfully uploaded ${file.name} for ${docType}!`,
        severity: "success",
      });

      // Optimistically update document status in drawer state immediately
      const uploadedDocData = uploadRes?.data?.successful?.[0] || uploadRes?.data || {};
      setLocalCandidate((prev) => {
        if (!prev) return prev;
        const updatedDocs = (prev.required_documents || []).map((d) => {
          const dType = d.document_type || d.doc_type;
          if (dType === docType) {
            return {
              ...d,
              status: "SUBMITTED",
              doc_path: uploadedDocData.s3_path || d.doc_path || "uploaded",
              document_id: uploadedDocData.document_id || d.document_id,
              file_name: file.name,
            };
          }
          return d;
        });
        return {
          ...prev,
          required_documents: updatedDocs,
        };
      });

      await reloadCandidateData();
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error("Error manually uploading file:", err);
      setFeedback({
        open: true,
        message: err.response?.data?.detail || err.message || "Failed to upload document.",
        severity: "error",
      });
    } finally {
      setUploadingDocType(null);
      event.target.value = "";
    }
  };

  const handleOpenReview = (doc) => {
    setReviewDialog({
      open: true,
      doc,
      status: "VERIFIED",
      reason: "All mandatory verification checks confirmed by recruiter.",
      submitting: false,
    });
  };

  const handleSubmitReview = async () => {
    const { doc, status, reason } = reviewDialog;
    if (!doc || !doc.document_id) {
      setFeedback({
        open: true,
        message: "No document record found to verify. Please upload a file first.",
        severity: "warning",
      });
      setReviewDialog((prev) => ({ ...prev, open: false }));
      return;
    }

    setReviewDialog((prev) => ({ ...prev, submitting: true }));
    try {
      await batchManualVerify(preOnboardingId, currentUserId, [
        {
          document_id: doc.document_id,
          is_verified: status === "VERIFIED",
          verification_reason: reason.trim() || `Marked as ${status} by recruiter`,
        },
      ]);
      setFeedback({
        open: true,
        message: `Document successfully marked as ${status}!`,
        severity: "success",
      });
      setReviewDialog({ open: false, doc: null, status: "VERIFIED", reason: "", submitting: false });

      // Optimistically update document status in drawer state immediately
      setLocalCandidate((prev) => {
        if (!prev) return prev;
        const updatedDocs = (prev.required_documents || []).map((d) => {
          if (d.document_id === doc.document_id || (d.document_type || d.doc_type) === (doc.document_type || doc.doc_type)) {
            return {
              ...d,
              status: status,
              is_verified: status === "VERIFIED",
              verification_status: status,
              verification_reason: reason.trim() || `Marked as ${status} by recruiter`,
            };
          }
          return d;
        });
        return {
          ...prev,
          required_documents: updatedDocs,
        };
      });

      await reloadCandidateData();
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error("Error submitting manual review:", err);
      setFeedback({
        open: true,
        message: err.response?.data?.detail || err.message || "Failed to submit review.",
        severity: "error",
      });
      setReviewDialog((prev) => ({ ...prev, submitting: false }));
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            onClose();
          }
        }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 540 },
            p: 3,
            bgcolor: "#F9FAFB",
            overflowY: "auto",
            overscrollBehavior: "contain",
          },
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={700} color="#111827">
              {activeCandidate.candidate_name || "Candidate Documents"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {activeCandidate.candidate_email || activeCandidate.email || "No email provided"}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Candidate Meta Info */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {activeCandidate.role || activeCandidate.position || activeCandidate.job_title ? (
            <Chip
              label={activeCandidate.role || activeCandidate.position || activeCandidate.job_title}
              size="small"
              sx={{ fontWeight: 600 }}
            />
          ) : null}
          {activeCandidate.job_code && (
            <Chip label={`Job: ${activeCandidate.job_code}`} size="small" variant="outlined" />
          )}
          {activeCandidate.hiring_type && (
            <Chip
              label={activeCandidate.hiring_type === "BLUE_COLLAR" ? "Blue Collar" : "White Collar"}
              size="small"
              variant="outlined"
            />
          )}
          <Chip
            label={statusInfo.label}
            size="small"
            sx={{ fontWeight: 600, bgcolor: statusInfo.bgcolor, color: statusInfo.color }}
          />
        </Box>

        {/* Sync Emails Banner */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            border: "1px solid #E5E7EB",
            bgcolor: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography variant="subtitle2" fontWeight={600} color="#111827">
              Email Replies & Attachments
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Check Nylas for newly received documents
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={handleSyncReplies}
            disabled={syncing || !preOnboardingId}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {syncing ? "Syncing..." : "Sync Replies"}
          </Button>
        </Paper>

        {/* Feedback Alert */}
        {feedback.open && (
          <Alert severity={feedback.severity} sx={{ mb: 2, borderRadius: 2 }} onClose={() => setFeedback((p) => ({ ...p, open: false }))}>
            {feedback.message}
          </Alert>
        )}

        {/* Follow-up / Reminder Cycle Status Banner */}
        {(() => {
          const followupCount = activeCandidate.followup_count ?? 0;
          const maxFollowupCount = activeCandidate.max_followup_count ?? 3;
          const isMaxFollowupsReached = followupCount >= maxFollowupCount;

          if (isMaxFollowupsReached) {
            return (
              <Alert
                severity="warning"
                icon={<LockIcon fontSize="inherit" />}
                sx={{ mb: 2.5, borderRadius: 2 }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  Max Follow-ups Reached ({followupCount}/{maxFollowupCount})
                </Typography>
                <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                  The candidate follow-up cycle has ended. Manual recruiter upload and manual verification are unlocked for all documents.
                </Typography>
              </Alert>
            );
          }

          return (
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                mb: 2.5,
                borderRadius: 2,
                bgcolor: "#F0FDF4",
                border: "1px solid #BBF7D0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AutoAwesomeIcon sx={{ color: "#16A34A", fontSize: 20 }} />
                <Box>
                  <Typography variant="caption" fontWeight={700} color="#166534" sx={{ display: "block" }}>
                    AI Automated Document Collection Active
                  </Typography>
                  <Typography variant="caption" color="#15803D">
                    Reminder emails sent: <strong>{followupCount} / {maxFollowupCount}</strong>
                  </Typography>
                </Box>
              </Box>
              <Chip
                label={`${followupCount}/${maxFollowupCount} Follow-ups`}
                size="small"
                sx={{ bgcolor: "#DCFCE7", color: "#15803D", fontWeight: 700, fontSize: "0.7rem" }}
              />
            </Paper>
          );
        })()}

        {/* Required Documents Section */}
        <Typography variant="subtitle1" fontWeight={700} color="#111827" sx={{ mb: 1.5 }}>
          Required Documents ({reqDocs.length})
        </Typography>

        {loadingDetails ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : reqDocs.length === 0 ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: "center", bgcolor: "#FFFFFF", borderRadius: 2, border: "1px solid #E5E7EB" }}>
            <DescriptionIcon sx={{ fontSize: 48, color: "#9CA3AF", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No document requirements found for this candidate.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={2}>
            {reqDocs.map((doc, idx) => {
              const docType = doc.document_type || doc.doc_type;
              const verificationMode = (doc.verification_mode || "AI").toUpperCase();
              const isManualDoc = verificationMode === "MANUAL";
              const isAiFailed =
                doc.status === "FAILED" ||
                doc.verification_status === "FAILED" ||
                doc.verification_status === "NOT_VERIFIED" ||
                doc.status === "REJECTED";

              let docStatus = doc.status || "NOT_SUBMITTED";
              if (doc.is_verified) {
                docStatus = "VERIFIED";
              } else if (isAiFailed) {
                docStatus = doc.status === "REJECTED" ? "REJECTED" : "AI_FAILED";
              }
              const dColor = statusColor(docStatus);
              const isUploadingThis = uploadingDocType === docType;
              const followupCount = activeCandidate.followup_count ?? 0;
              const maxFollowupCount = activeCandidate.max_followup_count ?? 3;
              const isMaxFollowupsReached = followupCount >= maxFollowupCount;
              const canManualUpload = isManualDoc || isMaxFollowupsReached;
              const canManualVerify = Boolean(doc.doc_path) && (isManualDoc || isMaxFollowupsReached || isAiFailed);

              return (
                <Paper
                  key={doc.document_id || idx}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid #E5E7EB",
                    bgcolor: "#FFFFFF",
                    transition: "border-color 0.2s",
                    "&:hover": { borderColor: "#CBD5E1" },
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
                        <Typography variant="subtitle2" fontWeight={700} color="#111827">
                          {doc.display_name || docType}
                        </Typography>
                        {doc.is_mandatory && (
                          <Chip label="Required" size="small" color="error" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Code: <code>{docType}</code>
                      </Typography>
                    </Box>

                    <Chip
                      label={dColor.label}
                      size="small"
                      sx={{ fontWeight: 600, bgcolor: dColor.bgcolor, color: dColor.color, height: 22, fontSize: "0.7rem" }}
                    />
                  </Box>

                  {/* Lifecycle Context Note */}
                  {isManualDoc ? (
                    <Typography variant="caption" sx={{ color: "#6B21A8", display: "block", mt: 0.5, mb: 1, fontWeight: 500 }}>
                      Recruiter verification configured. Upload and review are available.
                    </Typography>
                  ) : isAiFailed ? (
                    <Typography variant="caption" sx={{ color: "#DC2626", display: "block", mt: 0.5, mb: 1, fontWeight: 600 }}>
                      Document needs attention ({doc.verification_reason || "Verification required"}). Recruiter review is unlocked to verify or resolve.
                    </Typography>
                  ) : !isMaxFollowupsReached ? (
                    <Typography variant="caption" sx={{ color: "#6B7280", display: "block", mt: 0.5, mb: 1 }}>
                      Automated email reminders active ({followupCount}/{maxFollowupCount}). Recruiter verification unlocks after follow-ups complete.
                    </Typography>
                  ) : (
                    <Typography variant="caption" sx={{ color: "#B45309", display: "block", mt: 0.5, mb: 1, fontWeight: 500 }}>
                      Follow-up cycle completed ({followupCount}/{maxFollowupCount}). Recruiter upload and verification unlocked.
                    </Typography>
                  )}

                  {/* In-Screen Preview & Download for uploaded document */}
                  {doc.doc_path && (
                    <Box sx={{ mt: 1, mb: 1.5, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon fontSize="small" />}
                        onClick={() => {
                          setPreviewZoom(1);
                          setPreviewDoc(doc);
                        }}
                        sx={{
                          textTransform: "none",
                          color: "#1D4ED8",
                          borderColor: "#BFDBFE",
                          bgcolor: "#EFF6FF",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          borderRadius: 1.5,
                          px: 1.5,
                          py: 0.5,
                          "&:hover": {
                            bgcolor: "#DBEAFE",
                            borderColor: "#93C5FD",
                          },
                        }}
                      >
                        View / Download Document
                      </Button>
                      <Tooltip title="Download document file directly">
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadDoc(doc.doc_path, `${doc.doc_type || 'document'}`)}
                          sx={{
                            color: "#475569",
                            border: "1px solid #E2E8F0",
                            borderRadius: 1.5,
                            p: 0.5,
                            "&:hover": { bgcolor: "#F1F5F9", color: "#0F172A" },
                          }}
                        >
                          <DownloadIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}

                  <Divider sx={{ my: 1.5 }} />

                  {/* Action Buttons */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                    {/* Manual Upload Button */}
                    {!canManualUpload ? (
                      <Tooltip title={`Manual upload for AI documents will unlock after max email follow-ups (${followupCount}/${maxFollowupCount}) are reached.`}>
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<LockIcon fontSize="small" />}
                            disabled
                            sx={{ textTransform: "none", borderRadius: 1.5, fontSize: "0.75rem" }}
                          >
                            Upload Locked
                          </Button>
                        </span>
                      </Tooltip>
                    ) : (
                      <label>
                        <input
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg"
                          style={{ display: "none" }}
                          onChange={(e) => handleFileUpload(e, docType)}
                          disabled={isUploadingThis}
                        />
                        <Button
                          component="span"
                          size="small"
                          variant="outlined"
                          startIcon={isUploadingThis ? <CircularProgress size={14} /> : <CloudUploadIcon fontSize="small" />}
                          disabled={isUploadingThis}
                          sx={{ textTransform: "none", borderRadius: 1.5, fontSize: "0.75rem" }}
                        >
                          {isUploadingThis ? "Uploading..." : doc.doc_path ? "Re-upload" : "Upload File"}
                        </Button>
                      </label>
                    )}

                    {/* Manual Review Button */}
                    {doc.doc_path && (
                      !canManualVerify ? (
                        <Tooltip title={`Manual verification unlocks after email follow-ups (${followupCount}/${maxFollowupCount}) or if review is needed.`}>
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<LockIcon fontSize="small" />}
                              disabled
                              sx={{ textTransform: "none", borderRadius: 1.5, fontSize: "0.75rem" }}
                            >
                              Review Locked
                            </Button>
                          </span>
                        </Tooltip>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<VerifiedIcon fontSize="small" />}
                          onClick={() => handleOpenReview(doc)}
                          disabled={doc.is_verified || doc.status === "VERIFIED"}
                          sx={{
                            bgcolor: (doc.is_verified || doc.status === "VERIFIED") ? "#059669" : (isAiFailed ? "#DC2626" : "#111827"),
                            "&:hover": { bgcolor: (doc.is_verified || doc.status === "VERIFIED") ? "#047857" : (isAiFailed ? "#B91C1C" : "#374151") },
                            textTransform: "none",
                            borderRadius: 1.5,
                            fontSize: "0.75rem",
                            fontWeight: 600,
                          }}
                        >
                          {(doc.is_verified || doc.status === "VERIFIED") ? "Verified" : "Review / Verify"}
                        </Button>
                      )
                    )}
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Drawer>

      {/* Manual Verification Dialog */}
      <Dialog
        open={reviewDialog.open}
        onClose={() => setReviewDialog((prev) => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Manual Document Review & Verification
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {/* Document & Candidate Info Card */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "#F8FAFC" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="#0F172A">
                  {reviewDialog.doc?.display_name || reviewDialog.doc?.document_type || reviewDialog.doc?.doc_type}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Candidate: <strong>{activeCandidate.candidate_name || "N/A"}</strong> ({activeCandidate.candidate_email || activeCandidate.email || "No email"})
                </Typography>
              </Box>
              {reviewDialog.doc?.doc_path && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityIcon fontSize="small" />}
                  onClick={() => {
                    setPreviewZoom(1);
                    setPreviewDoc(reviewDialog.doc);
                  }}
                  sx={{
                    textTransform: "none",
                    borderRadius: 1.5,
                    fontSize: "0.75rem",
                    color: "#1D4ED8",
                    borderColor: "#BFDBFE",
                    bgcolor: "#EFF6FF",
                    "&:hover": { bgcolor: "#DBEAFE" },
                  }}
                >
                  View Document
                </Button>
              )}
            </Box>

            {/* AI verification note if exists */}
            {reviewDialog.doc?.verification_reason && (
              <Alert
                severity={
                  reviewDialog.doc?.status === "FAILED" ||
                  reviewDialog.doc?.verification_status === "FAILED" ||
                  reviewDialog.doc?.verification_status === "NOT_VERIFIED"
                    ? "warning"
                    : "info"
                }
                sx={{ mt: 1.5, py: 0.5, borderRadius: 1.5, fontSize: "0.8rem" }}
              >
                <strong>System Note:</strong> {reviewDialog.doc.verification_reason}
              </Alert>
            )}
          </Paper>

          {/* Extracted Document Fields Display */}
          {reviewDialog.doc?.extracted_data && typeof reviewDialog.doc.extracted_data === "object" && Object.keys(reviewDialog.doc.extracted_data).length > 0 ? (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "#FFFFFF" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                <Typography variant="caption" fontWeight={700} color="#475569" sx={{ letterSpacing: 0.5 }}>
                  DOCUMENT FIELDS & EXTRACTED DATA
                </Typography>
                <Chip
                  label={`${Object.keys(reviewDialog.doc.extracted_data).length} fields extracted`}
                  size="small"
                  sx={{ height: 20, fontSize: "0.68rem", bgcolor: "#EFF6FF", color: "#1D4ED8", fontWeight: 600 }}
                />
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                {Object.entries(reviewDialog.doc.extracted_data).map(([key, val]) => (
                  <Box key={key} sx={{ p: 1.25, bgcolor: "#F8FAFC", borderRadius: 1.5, border: "1px solid #E2E8F0" }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: "capitalize", display: "block", fontSize: "0.7rem", fontWeight: 600 }}>
                      {key.replace(/_/g, " ")}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="#0F172A" sx={{ mt: 0.25, wordBreak: "break-word" }}>
                      {String(val || "—")}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "#F8FAFC", textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No extracted fields available for this document. Verify details directly against the uploaded document file.
              </Typography>
            </Paper>
          )}

          <Typography variant="subtitle2" fontWeight={600} color="#1E293B">
            Verification Decision
          </Typography>

          <RadioGroup
            value={reviewDialog.status}
            onChange={(e) => setReviewDialog((prev) => ({ ...prev, status: e.target.value }))}
          >
            <FormControlLabel
              value="VERIFIED"
              control={<Radio color="success" />}
              label="Approve & Mark Verified (All details confirmed)"
            />
            <FormControlLabel
              value="REJECTED"
              control={<Radio color="error" />}
              label="Reject Document (Document illegible, wrong type, or invalid)"
            />
          </RadioGroup>

          <TextField
            label="Verification Notes / Recruiter Remarks"
            fullWidth
            multiline
            rows={2}
            placeholder="e.g. Details matched successfully or Document is expired/illegible."
            value={reviewDialog.reason}
            onChange={(e) => setReviewDialog((prev) => ({ ...prev, reason: e.target.value }))}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setReviewDialog((prev) => ({ ...prev, open: false }))}
            disabled={reviewDialog.submitting}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitReview}
            disabled={reviewDialog.submitting}
            sx={{
              bgcolor: reviewDialog.status === "VERIFIED" ? "#059669" : "#DC2626",
              "&:hover": { bgcolor: reviewDialog.status === "VERIFIED" ? "#047857" : "#B91C1C" },
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            {reviewDialog.submitting ? "Saving..." : `Confirm ${reviewDialog.status === "VERIFIED" ? "Approval" : "Rejection"}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* In-Screen Document Preview Modal */}
      <Dialog
        open={Boolean(previewDoc)}
        onClose={() => setPreviewDoc(null)}
        maxWidth="lg"
        fullWidth
        sx={{ zIndex: 1400 }}
        PaperProps={{
          sx: {
            height: "85vh",
            maxHeight: "920px",
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.4)",
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            py: 1.5,
            px: 2.5,
            bgcolor: "#0F172A",
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #334155",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                bgcolor: "rgba(56, 189, 248, 0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <DescriptionIcon sx={{ color: "#38BDF8", fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ color: "#F8FAFC", fontSize: "1rem" }}>
                {previewDoc?.display_name || previewDoc?.doc_type || "Document Preview"}
              </Typography>
              <Typography variant="caption" sx={{ color: "#94A3B8" }}>
                {localCandidate?.candidate_name ? `Candidate: ${localCandidate.candidate_name}` : "Pre-Onboarding Document"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Image Zoom controls if document is an image */}
            {previewDoc?.doc_path && isImageDoc(previewDoc.doc_path) && (
              <Box sx={{ display: "flex", alignItems: "center", bgcolor: "#1E293B", borderRadius: 1.5, p: 0.25, mr: 1 }}>
                <Tooltip title="Zoom out">
                  <IconButton
                    size="small"
                    onClick={() => setPreviewZoom((z) => Math.max(0.5, z - 0.25))}
                    disabled={previewZoom <= 0.5}
                    sx={{ color: "#94A3B8", "&:hover": { color: "#FFFFFF" } }}
                  >
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Typography variant="caption" sx={{ color: "#CBD5E1", px: 0.5, minWidth: 36, textAlign: "center", fontSize: "0.75rem" }}>
                  {Math.round(previewZoom * 100)}%
                </Typography>
                <Tooltip title="Zoom in">
                  <IconButton
                    size="small"
                    onClick={() => setPreviewZoom((z) => Math.min(3, z + 0.25))}
                    disabled={previewZoom >= 3}
                    sx={{ color: "#94A3B8", "&:hover": { color: "#FFFFFF" } }}
                  >
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reset Zoom">
                  <IconButton
                    size="small"
                    onClick={() => setPreviewZoom(1)}
                    sx={{ color: "#94A3B8", "&:hover": { color: "#FFFFFF" } }}
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {previewDoc?.doc_path && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon fontSize="small" />}
                onClick={() => handleDownloadDoc(previewDoc.doc_path, `${previewDoc.doc_type || "document"}`)}
                sx={{
                  color: "#E2E8F0",
                  borderColor: "#334155",
                  textTransform: "none",
                  fontSize: "0.75rem",
                  borderRadius: 1.5,
                  "&:hover": { borderColor: "#64748B", bgcolor: "rgba(255,255,255,0.08)" },
                }}
              >
                Download
              </Button>
            )}

            {previewDoc?.doc_path && (
              <Tooltip title="Open in new window">
                <IconButton
                  size="small"
                  onClick={() => window.open(previewDoc.doc_path, "_blank")}
                  sx={{ color: "#94A3B8", "&:hover": { color: "#FFFFFF" } }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            <IconButton
              onClick={() => setPreviewDoc(null)}
              size="small"
              sx={{ color: "#94A3B8", "&:hover": { color: "#FFFFFF" }, ml: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            bgcolor: "#0F172A",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {previewDoc?.doc_path ? (
            isImageDoc(previewDoc.doc_path) ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 3,
                  overflow: "auto",
                  bgcolor: "#020617",
                }}
              >
                <img
                  src={previewDoc.doc_path}
                  alt={previewDoc.display_name || previewDoc.doc_type || "Document Preview"}
                  style={{
                    transform: `scale(${previewZoom})`,
                    transformOrigin: "center center",
                    transition: "transform 0.15s ease-out",
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    borderRadius: 6,
                    boxShadow: "0 20px 40px rgba(0,0,0,0.6)",
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ flex: 1, width: "100%", height: "100%", position: "relative", bgcolor: "#FFFFFF" }}>
                <iframe
                  src={previewDoc.doc_path}
                  title={previewDoc.display_name || previewDoc.doc_type || "Document Preview"}
                  width="100%"
                  height="100%"
                  style={{
                    border: "none",
                    display: "block",
                    width: "100%",
                    height: "100%",
                    backgroundColor: "#FFFFFF",
                  }}
                />
              </Box>
            )
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
                color: "#94A3B8",
              }}
            >
              <DescriptionIcon sx={{ fontSize: 64, color: "#475569", mb: 2 }} />
              <Typography variant="h6" color="#E2E8F0">
                No Document File Available
              </Typography>
              <Typography variant="body2" color="#64748B">
                The document path could not be loaded or has expired.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CandidateDocumentDrawer;
